import React, { useState } from 'react';
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  User,
  FileText,
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Bell,
  Target,
  DollarSign,
  Plane,
  GraduationCap,
  MapPin,
  Mail,
  Phone,
  Send,
  X,
  Video,
  MessageSquare,
  Trash2
} from 'lucide-react';
import { Deadline, DeadlineNotification } from '../../types';
import { mockDeadlines, mockDeadlineNotifications } from '../../data/mockData';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddDeadlineModal from '../Modals/AddDeadlineModal';
import { useCalendar } from '../../hooks/useCalendar';
import { useLeads } from '../../hooks/useSupabase';
import { supabase } from '../../lib/supabase';

const Deadlines: React.FC = () => {
  const { user } = useAuth();
  const { events, loading: eventsLoading, fetchEvents } = useCalendar();
  const { leads, loading: leadsLoading, fetchLeads } = useLeads();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [loadingDeadlines, setLoadingDeadlines] = useState(true);
  const [allDataLoaded, setAllDataLoaded] = useState(false);

  // Fetch deadlines from database
  const fetchDeadlines = React.useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoadingDeadlines(true);
      const { data, error } = await supabase
        .from('deadlines')
        .select('id, title, description, type, due_date, priority, affected_users, created_by, created_at, is_active, reminder_days, category, related_universities, related_countries, notes, contact_person, contact_email')
        .eq('is_active', true)
        .order('due_date', { ascending: true });

      if (error) {
        console.error('Error fetching deadlines:', error);
        return;
      }

      // Convert database format to component format
      const formattedDeadlines: Deadline[] = (data || []).map(d => ({
        id: d.id,
        title: d.title,
        description: d.description,
        type: d.type,
        dueDate: new Date(d.due_date),
        priority: d.priority,
        affectedUsers: d.affected_users || [],
        createdBy: d.created_by,
        createdAt: new Date(d.created_at),
        isActive: d.is_active,
        reminderDays: d.reminder_days || [],
        category: d.category,
        relatedUniversities: d.related_universities || [],
        relatedCountries: d.related_countries || [],
        notes: d.notes,
        contactPerson: d.contact_person,
        contactEmail: d.contact_email
      }));

      setDeadlines(formattedDeadlines);
    } catch (err) {
      console.error('Error fetching deadlines:', err);
    } finally {
      setLoadingDeadlines(false);
    }
  }, [user?.id]);

  // Fetch data when component mounts - parallel fetching
  React.useEffect(() => {
    if (!user?.id) return;

    const loadAllData = async () => {
      setAllDataLoaded(false);
      await Promise.all([
        fetchDeadlines(),
        fetchEvents(),
        fetchLeads()
      ]);
      setAllDataLoaded(true);
    };

    loadAllData();
  }, [user?.id, fetchDeadlines]);

  // Global deadline listener
  React.useEffect(() => {
    const handleNewDeadline = (event: any) => {
      // Refetch deadlines to get the latest data
      fetchDeadlines();
    };

    window.addEventListener('newDeadline', handleNewDeadline);
    return () => window.removeEventListener('newDeadline', handleNewDeadline);
  }, [user?.id]);

  // Follow-up gerektiren lead'leri deadline olarak göster (memoized)
  const followUpDeadlines = React.useMemo(() => {
    if (!leads || !user?.id) return [];

    return leads.filter(lead =>
      lead.follow_up_required === true && lead.follow_up_date
    ).map(lead => ({
      id: `followup-${lead.id}`,
      title: `Follow-up: ${lead.first_name} ${lead.last_name}`,
      description: lead.follow_up_notes || 'Lead takip görüşmesi yapılacak',
      type: 'other' as const,
      dueDate: new Date(lead.follow_up_date),
      priority: lead.priority === 'high' ? 'high' as const :
        lead.priority === 'medium' ? 'medium' as const : 'low' as const,
      affectedUsers: [user.id],
      createdBy: lead.meeting_consultant || 'Sistem',
      createdAt: new Date(lead.created_at),
      isActive: true,
      reminderDays: [7, 3, 1],
      category: 'general' as const,
      contactPerson: lead.meeting_consultant,
      contactEmail: user.email,
      leadId: lead.id,
      leadPhone: lead.phone,
      leadEmail: lead.email
    } as unknown as Deadline));
  }, [leads, user?.id, user?.email]);

  // Yaklaşan etkinlikleri deadline olarak göster (7 gün içinde) (memoized)
  const eventDeadlines = React.useMemo(() => {
    if (!events || !user?.id) return [];

    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate >= now && eventDate <= sevenDaysFromNow;
    }).map(event => ({
      id: `event-${event.id}`,
      title: `Etkinlik: ${event.title}`,
      description: event.description || 'Takvim etkinliği',
      type: 'other' as const,
      dueDate: new Date(event.start_time),
      priority: 'medium' as const,
      affectedUsers: [user.id],
      createdBy: event.created_by || 'Sistem',
      createdAt: new Date(event.created_at),
      isActive: true,
      reminderDays: [3, 1],
      category: 'general' as const,
      contactPerson: event.created_by,
      eventId: event.id,
      eventLocation: event.location,
      eventMeetingLink: event.meeting_link
    } as unknown as Deadline));
  }, [events, user?.id]);

  // Kullanıcıya özel deadline'ları filtrele (memoized)
  const userCustomDeadlines = React.useMemo(() => {
    if (!user?.id) return [];

    return deadlines.filter(deadline =>
      deadline.affectedUsers.includes(user.id)
    );
  }, [deadlines, user?.id]);

  // Tüm deadline türlerini birleştir (memoized) - follow-up'lar HARİÇ
  const allDeadlines = React.useMemo(() => {
    return [
      ...userCustomDeadlines,
      ...eventDeadlines
    ].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }, [userCustomDeadlines, eventDeadlines]);

  const userNotifications = mockDeadlineNotifications.filter(notif =>
    notif.userId === user?.id
  );

  // Filtrelenmiş deadline'lar (memoized)
  const filteredDeadlines = React.useMemo(() => {
    const searchLower = searchTerm.toLowerCase();

    return allDeadlines.filter(deadline => {
      const matchesSearch = !searchTerm ||
        deadline.title.toLowerCase().includes(searchLower) ||
        deadline.description.toLowerCase().includes(searchLower);

      const matchesType = typeFilter === 'all' || deadline.type === typeFilter;
      const matchesPriority = priorityFilter === 'all' || deadline.priority === priorityFilter;
      const matchesCategory = categoryFilter === 'all' ||
        (categoryFilter === 'followup' && deadline.id.startsWith('followup-')) ||
        (categoryFilter === 'event' && deadline.id.startsWith('event-')) ||
        (categoryFilter === 'custom' && !deadline.id.startsWith('followup-') && !deadline.id.startsWith('event-'));

      return matchesSearch && matchesType && matchesPriority && matchesCategory;
    });
  }, [allDeadlines, searchTerm, typeFilter, priorityFilter, categoryFilter]);

  const getDaysLeft = (dueDate: Date) => {
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleSaveDeadline = (deadlineData: any) => {
    // Refetch deadlines to show the new one
    fetchDeadlines();
    setShowAddModal(false);
  };

  const handleDeleteDeadline = async (deadlineId: string) => {
    if (!await window.appConfirm('Bu deadline\'i silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('deadlines')
        .delete()
        .eq('id', deadlineId);

      if (error) {
        console.error('Error deleting deadline:', error);
        alert('Deadline silinirken bir hata oluştu.');
        return;
      }

      // Refetch deadlines to update the list
      fetchDeadlines();
      alert('Deadline başarıyla silindi.');
    } catch (err) {
      console.error('Error deleting deadline:', err);
      alert('Deadline silinirken bir hata oluştu.');
    }
  };

  const getPriorityColor = (priority: Deadline['priority']) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 border-red-200 dark:border-red-800';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400 border-orange-200 dark:border-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 border-green-200 dark:border-green-800';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
  };

  const getTypeIcon = (type: Deadline['type']) => {
    switch (type) {
      case 'application': return GraduationCap;
      case 'document': return FileText;
      case 'payment': return DollarSign;
      case 'visa': return Plane;
      case 'enrollment': return CheckCircle;
      default: return Clock;
    }
  };

  const getTypeColor = (type: Deadline['type']) => {
    switch (type) {
      case 'application': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
      case 'document': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400';
      case 'payment': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'visa': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400';
      case 'enrollment': return 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getTypeText = (type: Deadline['type']) => {
    switch (type) {
      case 'application': return 'Başvuru';
      case 'document': return 'Belge';
      case 'payment': return 'Ödeme';
      case 'visa': return 'Vize';
      case 'enrollment': return 'Kayıt';
      default: return 'Diğer';
    }
  };

  const getPriorityText = (priority: Deadline['priority']) => {
    switch (priority) {
      case 'critical': return 'Kritik';
      case 'high': return 'Yüksek';
      case 'medium': return 'Orta';
      case 'low': return 'Düşük';
      default: return priority;
    }
  };

  // Categorized deadlines (memoized)
  const urgentDeadlines = React.useMemo(() => {
    return filteredDeadlines.filter(deadline => {
      const daysLeft = getDaysLeft(deadline.dueDate);
      return daysLeft <= 7 && daysLeft >= 0;
    });
  }, [filteredDeadlines]);

  const upcomingDeadlines = React.useMemo(() => {
    return filteredDeadlines.filter(deadline => {
      const daysLeft = getDaysLeft(deadline.dueDate);
      return daysLeft > 7 && daysLeft <= 30;
    });
  }, [filteredDeadlines]);

  const futureDeadlines = React.useMemo(() => {
    return filteredDeadlines.filter(deadline => {
      const daysLeft = getDaysLeft(deadline.dueDate);
      return daysLeft > 30;
    });
  }, [filteredDeadlines]);

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {(loadingDeadlines || leadsLoading || eventsLoading) && !allDataLoaded && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Hatırlatıcılar yükleniyor...</span>
        </div>
      )}

      {/* Header */}
      {allDataLoaded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tüm Hatırlatıcılar</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Follow-up'lar, etkinlikler ve özel deadline'larınız • Toplam {filteredDeadlines.length} hatırlatıcı
            </p>
          </div>
          {user?.role !== 'student' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Özel Deadline Ekle</span>
            </button>
          )}
        </div>

      )}

      {/* Stats */}
      {allDataLoaded && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{urgentDeadlines.length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Acil (7 gün)</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <Target className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{followUpDeadlines.length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Follow-up'lar</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <Calendar className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{eventDeadlines.length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Yaklaşan Etkinlikler</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <Clock className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{userCustomDeadlines.length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Özel Deadline'lar</p>
              </div>
            </div>
          </div>
        </div>

      )}

      {/* Filters */}
      {allDataLoaded && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Hatırlatıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tüm Kategoriler</option>
              <option value="followup">Follow-up'lar</option>
              <option value="event">Etkinlikler</option>
              <option value="custom">Özel Deadline'lar</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tüm Türler</option>
              <option value="application">Başvuru</option>
              <option value="document">Belge</option>
              <option value="payment">Ödeme</option>
              <option value="visa">Vize</option>
              <option value="enrollment">Kayıt</option>
              <option value="other">Diğer</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tüm Öncelikler</option>
              <option value="critical">Kritik</option>
              <option value="high">Yüksek</option>
              <option value="medium">Orta</option>
              <option value="low">Düşük</option>
            </select>

            <button className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-lg transition-colors">
              <Filter className="w-4 h-4" />
              <span>Filtrele</span>
            </button>
          </div>
        </div>

      )}

      {/* Urgent Deadlines */}
      {allDataLoaded && urgentDeadlines.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            <h3 className="text-lg font-semibold text-red-900 dark:text-red-300">Acil Hatırlatıcılar (7 gün içinde)</h3>
          </div>
          <div className="space-y-4">
            {urgentDeadlines.map((deadline) => {
              const daysLeft = getDaysLeft(deadline.dueDate);
              const TypeIcon = getTypeIcon(deadline.type);
              const isFollowUp = deadline.id.startsWith('followup-');
              const isEvent = deadline.id.startsWith('event-');

              return (
                <div key={deadline.id} className="bg-white dark:bg-gray-900 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getTypeColor(deadline.type)}`}>
                        <TypeIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{deadline.title}</h4>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">{deadline.description}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-500">
                          <span>Tarih: {format(deadline.dueDate, 'dd MMMM yyyy', { locale: tr })}</span>
                          <span>Kategori: {isFollowUp ? 'Follow-up' : isEvent ? 'Etkinlik' : deadline.category}</span>
                          {deadline.contactPerson && (
                            <span>İletişim: {deadline.contactPerson}</span>
                          )}
                        </div>

                        {/* Follow-up için özel işlemler */}
                        {isFollowUp && (deadline as any).leadPhone && (
                          <div className="mt-3 flex items-center space-x-2">
                            <button
                              onClick={() => {
                                if (window.makeKarelCall) {
                                  window.makeKarelCall((deadline as any).leadPhone);
                                }
                              }}
                              className="flex items-center space-x-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
                            >
                              <Phone className="w-3 h-3" />
                              <span>Ara</span>
                            </button>
                            {(deadline as any).leadEmail && (
                              <button
                                onClick={() => {
                                  const email = (deadline as any).leadEmail;
                                  const subject = encodeURIComponent('Pisa Educazione - Follow Up Görüşmesi');
                                  const body = encodeURIComponent(`Merhaba,\n\nDaha önce konuştuğumuz eğitim fırsatları hakkında takip görüşmesi yapmak isterim.\n\nSaygılarımla,\n${user?.name}`);
                                  window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
                                }}
                                className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
                              >
                                <Mail className="w-3 h-3" />
                                <span>Email</span>
                              </button>
                            )}
                          </div>
                        )}

                        {/* Etkinlik için özel işlemler */}
                        {isEvent && (
                          <div className="mt-3 flex items-center space-x-2">
                            {(deadline as any).eventMeetingLink && (
                              <button
                                onClick={() => window.open((deadline as any).eventMeetingLink, '_blank')}
                                className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
                              >
                                <Video className="w-3 h-3" />
                                <span>Toplantıya Katıl</span>
                              </button>
                            )}
                            {(deadline as any).eventLocation && (
                              <span className="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400">
                                <MapPin className="w-3 h-3" />
                                <span>{(deadline as any).eventLocation}</span>
                              </span>
                            )}
                          </div>
                        )}


                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${daysLeft <= 1 ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                        daysLeft <= 3 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                        }`}>
                        {daysLeft <= 0 ? 'Süresi Doldu' :
                          daysLeft === 1 ? '1 gün kaldı' :
                            `${daysLeft} gün kaldı`}
                      </span>
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(deadline.priority)}`}>
                          {getPriorityText(deadline.priority)}
                        </span>
                      </div>
                      {/* Kategori göstergesi */}
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isFollowUp ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                          isEvent ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                          }`}>
                          {isFollowUp ? 'Follow-up' : isEvent ? 'Etkinlik' : 'Deadline'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Reminders */}
      {allDataLoaded && (
        <div className="space-y-4">
          {filteredDeadlines.map((deadline) => {
            const daysLeft = getDaysLeft(deadline.dueDate);
            const TypeIcon = getTypeIcon(deadline.type);
            const isUrgent = daysLeft <= 7 && daysLeft >= 0;
            const isFollowUp = deadline.id.startsWith('followup-');
            const isEvent = deadline.id.startsWith('event-');

            return (
              <div key={deadline.id} className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 hover:shadow-lg transition-shadow ${isUrgent ? 'ring-2 ring-red-200 dark:ring-red-800' : ''
                }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getTypeColor(deadline.type)}`}>
                      <TypeIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{deadline.title}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(deadline.type)}`}>
                          {getTypeText(deadline.type)}
                        </span>
                        {/* Kategori göstergesi */}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isFollowUp ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                          isEvent ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                          }`}>
                          {isFollowUp ? 'Follow-up' : isEvent ? 'Etkinlik' : 'Deadline'}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mb-3">{deadline.description}</p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Tarih Bilgileri</p>
                          <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {isFollowUp ? 'Follow-up Tarihi' : isEvent ? 'Etkinlik Tarihi' : 'Son Tarih'}:
                                {format(deadline.dueDate, 'dd MMMM yyyy HH:mm', { locale: tr })}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Clock className="w-4 h-4" />
                              <span>Kalan Süre: {daysLeft <= 0 ? 'Süresi Doldu' : `${daysLeft} gün`}</span>
                            </div>
                          </div>
                        </div>

                        {deadline.contactPerson && (
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">İletişim</p>
                            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                              <div className="flex items-center space-x-2">
                                <User className="w-4 h-4" />
                                <span>{deadline.contactPerson}</span>
                              </div>
                              {deadline.contactEmail && (
                                <div className="flex items-center space-x-2">
                                  <Mail className="w-4 h-4" />
                                  <span>{deadline.contactEmail}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Follow-up için özel işlemler */}
                      {isFollowUp && (
                        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Lead Follow-up</p>
                              <p className="text-xs text-blue-700 dark:text-blue-400">
                                Lead Score: {(leads || []).find(l => l.id === (deadline as any).leadId)?.lead_score || 0}/100
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {(deadline as any).leadPhone && (
                                <button
                                  onClick={() => {
                                    if (window.makeKarelCall) {
                                      window.makeKarelCall((deadline as any).leadPhone);
                                    }
                                  }}
                                  className="flex items-center space-x-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
                                >
                                  <Phone className="w-3 h-3" />
                                  <span>Ara</span>
                                </button>
                              )}
                              {(deadline as any).leadEmail && (
                                <button
                                  onClick={() => {
                                    const email = (deadline as any).leadEmail;
                                    const subject = encodeURIComponent('Pisa Educazione - Follow Up');
                                    const body = encodeURIComponent(`Merhaba,\n\nDaha önce konuştuğumuz eğitim fırsatları hakkında takip görüşmesi yapmak isterim.\n\nSaygılarımla,\n${user?.name}`);
                                    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
                                  }}
                                  className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
                                >
                                  <Mail className="w-3 h-3" />
                                  <span>Email</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Etkinlik için özel işlemler */}
                      {isEvent && (
                        <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-purple-900 dark:text-purple-300">Takvim Etkinliği</p>
                              {(deadline as any).eventLocation && (
                                <p className="text-xs text-purple-700 dark:text-purple-400">
                                  Konum: {(deadline as any).eventLocation}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {(deadline as any).eventMeetingLink && (
                                <button
                                  onClick={() => window.open((deadline as any).eventMeetingLink, '_blank')}
                                  className="flex items-center space-x-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
                                >
                                  <Video className="w-3 h-3" />
                                  <span>Katıl</span>
                                </button>
                              )}
                              <button
                                onClick={() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'calendar' }))}
                                className="flex items-center space-x-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors"
                              >
                                <Calendar className="w-3 h-3" />
                                <span>Takvimde Aç</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {deadline.notes && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Notlar</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{deadline.notes}</p>
                        </div>
                      )}



                      {deadline.relatedUniversities && deadline.relatedUniversities.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">İlgili Üniversiteler</p>
                          <div className="flex flex-wrap gap-2">
                            {deadline.relatedUniversities.slice(0, 3).map((uni, index) => (
                              <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 text-xs rounded-full">
                                {uni}
                              </span>
                            ))}
                            {deadline.relatedUniversities.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 text-xs rounded-full">
                                +{deadline.relatedUniversities.length - 3} daha
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-2 ${daysLeft <= 0 ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                      daysLeft <= 3 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                        daysLeft <= 7 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                          'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      }`}>
                      {daysLeft <= 0 ? 'Süresi Doldu' :
                        daysLeft === 1 ? '1 gün kaldı' :
                          `${daysLeft} gün kaldı`}
                    </span>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(deadline.priority)}`}>
                        {getPriorityText(deadline.priority)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mt-3">
                      {!isFollowUp && !isEvent && (
                        <>
                          <button className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDeadline(deadline.id)}
                            className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {user?.role === 'admin' && (isFollowUp || isEvent) && (
                        <button
                          onClick={() => handleDeleteDeadline(deadline.id)}
                          className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {deadline.contactEmail && (
                        <button className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors">
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      )}

      {allDataLoaded && filteredDeadlines.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-gray-500 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Hatırlatıcı bulunamadı</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Arama kriterlerinize uygun hatırlatıcı bulunmuyor.
          </p>
        </div>
      )}

      {/* Add Deadline Modal */}
      <AddDeadlineModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveDeadline}
      />
    </div>
  );
};

export default Deadlines;
